/**
 * Modelo de control de stock por nicho.
 * Un solo ledger (misma row); distintas UIs según perfil + capabilities.
 *
 * - ingredient: gastronomía con BOM — stock = insumos; venta descuenta vía receta
 * - sku: retail/moda/etc. — stock = producto/variante; venta descuenta SKU
 * - hybrid: food retail (almacén) sin BOM o con BOM parcial — SKU de venta + insumos opcionales
 */
import type { BusinessProfile } from "@/lib/business-profile";
import { BUSINESS_PROFILE_LABELS } from "@/lib/business-profile";

const GASTRO_BOM_PROFILES = new Set<BusinessProfile>(["restaurant", "fast_food", "cafe"]);

const SKU_FIRST_PROFILES = new Set<BusinessProfile>([
  "fashion",
  "bazar",
  "pet",
  "pharmacy",
  "catalog",
  "digital_products",
]);

export type StockControlMode = "ingredient" | "sku" | "hybrid";

function normalizeProfile(profile?: string | null): BusinessProfile {
  if (profile && profile in BUSINESS_PROFILE_LABELS) return profile as BusinessProfile;
  return "catalog";
}

/** Perfiles donde la venta típica se rige por receta BOM (platos). */
export function isGastroBomProfile(profile?: string | null): boolean {
  return GASTRO_BOM_PROFILES.has(normalizeProfile(profile));
}

/** Perfiles donde el stock de venta es SKU (producto/variante). */
export function isSkuFirstProfile(profile?: string | null): boolean {
  return SKU_FIRST_PROFILES.has(normalizeProfile(profile));
}

/**
 * Modo de control de stock del tenant.
 * Requiere `inventory_bom` activo para modo ingredient en gastro.
 */
export function resolveStockControlMode(
  profile?: string | null,
  opts?: { inventoryBom?: boolean },
): StockControlMode {
  const p = normalizeProfile(profile);
  const bom = opts?.inventoryBom === true;

  if (GASTRO_BOM_PROFILES.has(p) && bom) return "ingredient";
  if (GASTRO_BOM_PROFILES.has(p) && !bom) return "hybrid"; // gastro sin BOM: stock de producto hasta activar recetas
  if (p === "market" || p === "dietetics") return bom ? "hybrid" : "sku";
  if (SKU_FIRST_PROFILES.has(p)) return "sku";
  return "sku";
}

/** ¿La venta debe bloquear/descontar por insumos BOM? */
export function sellStockDrivenByBom(mode: StockControlMode): boolean {
  return mode === "ingredient" || mode === "hybrid";
}

/** ¿Inventario principal opera sobre insumos? */
export function inventoryControlsIngredients(mode: StockControlMode): boolean {
  return mode === "ingredient";
}

/** ¿Inventario principal opera sobre productos/variantes? */
export function inventoryControlsSkus(mode: StockControlMode): boolean {
  return mode === "sku" || mode === "hybrid";
}

export function inventoryNavLabel(mode: StockControlMode): string {
  if (mode === "ingredient") return "Stock (insumos)";
  if (mode === "hybrid") return "Inventario";
  return "Inventario";
}

export function inventoryPageTitle(mode: StockControlMode): string {
  if (mode === "ingredient") return "Stock de insumos";
  return "Inventario";
}

export function inventoryPageSubtitle(mode: StockControlMode): string {
  if (mode === "ingredient") {
    return "Mermas, conteos y ajustes sobre insumos. La entrada llega por Abastecimiento → Recepciones; la venta descuenta vía receta.";
  }
  if (mode === "hybrid") {
    return "Stock de productos a la venta e insumos opcionales. Abastecimiento carga mercadería; la venta descuenta el SKU o la receta según el producto.";
  }
  return "Stock de productos y variantes (talle/color). Abastecimiento carga mercadería; la venta descuenta el SKU.";
}

export function insumosNavLabel(profile?: string | null): string {
  if (isGastroBomProfile(profile)) return "Ingredientes";
  return "Insumos";
}

/** ¿Abastecimiento / recepciones priorizan insumos? */
export function supplyReceivesIngredients(mode: StockControlMode): boolean {
  return mode === "ingredient";
}

/** ¿Abastecimiento / recepciones priorizan productos (SKU)? */
export function supplyReceivesSkus(mode: StockControlMode): boolean {
  return mode === "sku" || mode === "hybrid";
}

export type NicheStockImpact = {
  mode: StockControlMode;
  sell: string;
  inventory: string;
  supply: string;
  productForm: string;
  gap?: string;
};

/**
 * Impacto operativo del modelo de stock por nicho (copy / docs / UI).
 * Un solo ledger conceptual: la venta descuenta una vez; cambia *qué* se descuenta.
 */
export function describeNicheStockImpact(
  profile?: string | null,
  opts?: { inventoryBom?: boolean },
): NicheStockImpact {
  const mode = resolveStockControlMode(profile, opts);
  const p = normalizeProfile(profile);

  if (mode === "ingredient") {
    return {
      mode,
      sell: "La venta descuenta insumos vía receta BOM; el plato se agota por rinde.",
      inventory: "Mermas y conteos sobre insumos (no stock unitario del plato).",
      supply: "Recepciones cargan insumos; alimentan el rinde de todas las recetas.",
      productForm: "Sin stock unitario del producto: se define la receta (BOM).",
    };
  }

  if (mode === "hybrid") {
    const gastro = isGastroBomProfile(p);
    return {
      mode,
      sell: gastro
        ? "Sin BOM activo: la venta puede descontar stock del producto hasta activar recetas."
        : "SKU a la venta; productos con receta también consumen insumos al vender.",
      inventory: "Ajustes de productos y, si hay BOM, también de insumos.",
      supply: "Recepciones de mercadería (productos) e insumos opcionales para recetas.",
      productForm: "Stock unitario del SKU; receta BOM opcional por producto.",
      gap: gastro
        ? "Activá inventory_bom para pasar a control puro por insumos."
        : undefined,
    };
  }

  // sku
  const fashionLike = p === "fashion";
  return {
    mode,
    sell: "La venta descuenta el SKU (producto). En moda el stock fino es por variante (talle/color).",
    inventory: "Ajustes y CSV sobre productos; variantes se editan en el producto.",
    supply: "Recepciones cargan stock de productos (SKU).",
    productForm: "Stock actual / mínimo del producto (y por variante si aplica).",
    gap: fashionLike
      ? "Recepciones aún no discriminan talle/color: el ingreso va al producto; el desglose por variante se ajusta en Catalogación."
      : p === "digital_products"
        ? "Productos digitales: stock suele ser irrelevante o ilimitado."
        : undefined,
  };
}

export function abastecimientoBannerCopy(mode: StockControlMode): {
  title: string;
  body: string;
} {
  if (mode === "ingredient") {
    return {
      title: "Recepción de insumos",
      body: "El stock entra a ingredientes y sube el rinde de las recetas / BOM de cada plato.",
    };
  }
  if (mode === "hybrid") {
    return {
      title: "Recepción de mercadería",
      body: "Cargá productos a la venta y, si usás recetas, también insumos. Un solo flujo de entrada.",
    };
  }
  return {
    title: "Recepción de productos",
    body: "El stock entra al SKU del producto. La venta lo descuenta al entregar el pedido.",
  };
}
